import React, { useState, useContext, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  Fade,
  Slide,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  LockOpen,
  Security,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { UserContext } from '../contexts/userContext';
import { Redirect, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';

export default function ModernSignIn() {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [step, setStep] = useState('login'); // 'login' | 'otp'
  const [otpValue, setOtpValue] = useState('');
  const pendingEmailRef = useRef('');

  const loginURL = `${process.env.REACT_APP_SERVER_API}/users/login`;

  const {
    setUserName,
    setLoginStatus,
    setRole,
    setUserEmail,
    loginStatus,
    setReferTo,
    setFullAccess,
    setUser,
  } = useContext(UserContext);

  const location = useLocation();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({});

  const finalizeLogin = (result) => {
    const user = result;
    const check1 =
      user.role === 'SuperAdmin' ||
      user.role === 'Admin' ||
      (user.role === 'User' && user.fullAccess)
        ? true
        : false;

    const limits = {
      edit_user: check1 || user.limits.edit_user ? true : false,
      edit_group: check1 || user.limits.edit_group ? true : false,
      view_machine: check1 || user.limits.view_machine ? true : false,
      edit_machine: check1 || user.limits.edit_machine ? true : false,
      view_plano: check1 || user.limits.view_plano ? true : false,
      edit_plano: check1 || user.limits.edit_plano ? true : false,
      view_ss: check1 || user.limits.view_ss ? true : false,
      edit_ss: check1 || user.limits.edit_ss ? true : false,
      view_prod: check1 || user.limits.view_prod ? true : false,
      edit_prod: check1 || user.limits.edit_prod ? true : false,
      view_cate: check1 || user.limits.view_cate ? true : false,
      edit_cate: check1 || user.limits.edit_cate ? true : false,
      view_brand: check1 || user.limits.view_brand ? true : false,
      edit_brand: check1 || user.limits.edit_brand ? true : false,
      view_receipt: check1 || user.limits.view_receipt ? true : false,
      edit_receipt: check1 || user.limits.edit_receipt ? true : false,
      view_promo: check1 || user.limits.view_promo ? true : false,
      edit_promo: check1 || user.limits.edit_promo ? true : false,
      view_trans: check1 || user.limits.view_trans ? true : false,
      view_report: check1 || user.limits.view_report ? true : false,
      view_member: check1 || user.limits.view_member ? true : false,
      edit_member: check1 || user.limits.edit_member ? true : false,
    };

    localStorage.setItem('user', JSON.stringify(result));
    localStorage.setItem('limits', JSON.stringify(limits));

    const fullName = result.firstname + ' ' + result.lastname;
    setLoginStatus(true);
    setUserName(fullName);
    setUserEmail(result.email);
    setRole(result.role);
    setReferTo(result.referTo);
    setFullAccess(result.fullAccess);
    setUser(result);
    window.location.reload();
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    setErrorMessage('');

    const email = getValues('email');
    const password = getValues('password');

    try {
      const res = await axios.post(
        loginURL,
        { password: password, email: email.toLowerCase() },
        { withCredentials: true }
      );

      if (res.data.requireOTP) {
        // 2FA enabled — show OTP step
        pendingEmailRef.current = email.toLowerCase();
        setStep('otp');
        setIsLoading(false);
        return;
      }

      finalizeLogin(res.data.result);
    } catch (err) {
      setIsLoading(false);
      setLoginStatus(false);
      setUserName('');
      if (err.response) {
        setErrorMessage(err.response.data.message);
      } else {
        setErrorMessage('An error occurred. Please try again.');
      }
    }
  };

  const onSubmitOTP = async () => {
    if (!otpValue.trim()) return;
    setIsLoading(true);
    setErrorMessage('');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_API}/users/verify-otp`,
        { email: pendingEmailRef.current, otp: otpValue.trim() },
        { withCredentials: true }
      );
      finalizeLogin(res.data.result);
    } catch (err) {
      setIsLoading(false);
      if (err.response) {
        setErrorMessage(err.response.data.message);
      } else {
        setErrorMessage('An error occurred. Please try again.');
      }
    }
  };

  if (loginStatus === true) {
    return <Redirect to={location.state?.from || '/dashboard'} />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 50%, #334155 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.main} 100%)`,
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '100%',
          height: '100%',
          transform: 'translate(-50%, -50%)',
          background:
            theme.palette.mode === 'dark'
              ? `radial-gradient(circle, ${theme.alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`
              : `radial-gradient(circle, ${theme.alpha('#FFFFFF', 0.2)} 0%, transparent 70%)`,
          animation: 'pulse 15s ease-in-out infinite',
        },
        '@keyframes pulse': {
          '0%, 100%': {
            opacity: 1,
            transform: 'translate(-50%, -50%) scale(1)',
          },
          '50%': {
            opacity: 0.8,
            transform: 'translate(-50%, -50%) scale(1.1)',
          },
        },
      }}
    >
      <Container
        component="main"
        maxWidth="sm"
        sx={{
          transform: 'scale(0.8)',
          transformOrigin: 'center center',
        }}
      >
        <Slide direction="down" in={true} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              padding: { xs: 4, sm: 6 },
              background:
                theme.palette.mode === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(255, 255, 255, 0.3)'
              }`,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                  : '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 12px 48px 0 rgba(0, 0, 0, 0.5)'
                    : '0 12px 48px 0 rgba(31, 38, 135, 0.5)',
              },
            }}
          >
            {/* Logo and Title */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Fade in={true} timeout={1000}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    mb: 3,
                    boxShadow: `0 8px 24px ${theme.alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                >
                  <LockOpen sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Fade>

              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Welcome Back
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: '1.1rem' }}
              >
                Sign in to continue to your account
              </Typography>
            </Box>

            {/* Error Alert */}
            {errorMessage && (
              <Fade in={true}>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    animation: 'shake 0.5s',
                    '@keyframes shake': {
                      '0%, 100%': { transform: 'translateX(0)' },
                      '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-4px)' },
                      '20%, 40%, 60%, 80%': { transform: 'translateX(4px)' },
                    },
                  }}
                  onClose={() => setErrorMessage('')}
                >
                  {errorMessage}
                </Alert>
              </Fade>
            )}

            {/* OTP Form */}
            {step === 'otp' && (
              <Box>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Security sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Check your email
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    A 6-digit verification code has been sent to your email. It expires in 10 minutes.
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Verification Code"
                  value={otpValue}
                  onChange={(e) => setOtpValue(e.target.value)}
                  inputProps={{ maxLength: 6, style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.4rem' } }}
                  sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  autoFocus
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={onSubmitOTP}
                  disabled={isLoading || otpValue.trim().length < 6}
                  sx={{
                    py: 1.8,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    mb: 2,
                  }}
                >
                  {isLoading ? <CircularProgress size={28} sx={{ color: 'white' }} /> : 'Verify'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  onClick={() => { setStep('login'); setOtpValue(''); setErrorMessage(''); }}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Back to sign in
                </Button>
              </Box>
            )}

            {/* Login Form */}
            {step === 'login' && <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                autoComplete="email"
                autoFocus
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                    '&.Mui-focused': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address',
                  },
                })}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                    '&.Mui-focused': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{
                          transition: 'transform 0.3s',
                          '&:hover': {
                            transform: 'rotate(180deg)',
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                })}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <MuiLink
                  href="/forgetpassword"
                  variant="body2"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    fontWeight: 500,
                    transition: 'all 0.3s',
                    '&:hover': {
                      textDecoration: 'underline',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  Forgot password?
                </MuiLink>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.8,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  boxShadow: `0 4px 16px ${theme.alpha(theme.palette.primary.main, 0.4)}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${theme.alpha(theme.palette.primary.main, 0.5)}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  '&.Mui-disabled': {
                    background: theme.palette.action.disabledBackground,
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={28} sx={{ color: 'white' }} />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>}
          </Paper>
        </Slide>
      </Container>
    </Box>
  );
}
